import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import SetupGuide from './SetupGuide';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { 
  FiUpload, FiFile, FiCalendar, FiCheck, FiX, FiSettings, 
  FiKey, FiSave, FiAlertCircle, FiTrash2, FiDatabase, 
  FiEye, FiAlertTriangle, FiInfo, FiRefreshCw, FiCloud,
  FiDatabaseCloud, FiShield, FiLink
} = FiIcons;

export default function AdminPanel() {
  const { 
    files, 
    uploadFile, 
    terminMarktData,
    spotMarktData,
    provisionData,
    dataSource, 
    hasFile,
    dataValidation,
    performDataValidation,
    renderValue,
    resetAllGlobalData,
    getGlobalDataStatus,
    supabaseStatus,
    updateSupabaseConfig,
    disconnectSupabase
  } = useData();
  
  const { updateAccessCodes } = useAuth();
  
  const [uploading, setUploading] = useState({});
  const [uploadStatus, setUploadStatus] = useState({});
  const [accessCodes, setAccessCodes] = useState({
    user: 'CBUY-USER-2024',
    admin: 'CBUY-ADMIN-2024'
  });
  const [showSetupGuide, setShowSetupGuide] = useState(false);
  
  // State für manuelle Supabase Konfiguration
  // VORBELEGUNG mit den Hardcoded Werten, damit der User sieht, was genutzt wird
  const [supabaseConfig, setSupabaseConfig] = useState({
    url: 'https://cjtarocrupqnbwlrujpx.supabase.co',
    key: 'sb_publishable_WxfxSvPbAFeDN6BkFmHNVA_cVtqejI7'
  });

  // Datenstatus aktualisieren
  const [dataStatus, setDataStatus] = useState(null);
  
  useEffect(() => {
    const status = getGlobalDataStatus();
    setDataStatus(status);
  }, [dataSource, terminMarktData.length, spotMarktData.length, provisionData.length, getGlobalDataStatus]);

  // Die 3 Dateitypen
  const fileTypes = [
    { 
      key: 'terminMarkt', 
      label: 'Terminmarkt – Anbieterleitfaden', 
      description: 'TerminMarkt Anbieterleitfaden mit Blattschutz (XLSX)',
      expectedSheet: 'Gesamt',
      accept: '.xlsx,.xls',
      active: true
    },
    { 
      key: 'spotMarkt', 
      label: 'Spotmarkt – Anbieterleitfaden', 
      description: 'SpotMarkt Anbieterleitfaden mit Blattschutz (XLSX)',
      expectedSheet: 'Gesamt',
      accept: '.xlsx,.xls',
      active: true
    },
    { 
      key: 'provisions', 
      label: 'Provisionsdeckelung Aufschlag', 
      description: 'Provisions Deckelung Aufschlag (PDF)',
      expectedSheet: 'Erste Tabelle mit Kopfzeile',
      accept: '.pdf',
      active: true
    }
  ];

  const handleFileUpload = async (fileType, event) => {
    const file = event.target.files[0];
    if (!file) return;

    console.log(`🚀 Starte Upload für ${fileType}: ${file.name}`);
    setUploading(prev => ({ ...prev, [fileType]: true }));
    setUploadStatus(prev => ({ ...prev, [fileType]: null }));

    try {
      const result = await uploadFile(fileType, file);
      setUploadStatus(prev => ({ ...prev, [fileType]: 'success' }));
      console.log(`✅ Upload erfolgreich: ${result.providers.length} Versorger geladen`);
      
      // Cloud-Daten sind jetzt für alle Benutzer verfügbar
      if (supabaseStatus.connected) {
        console.log(`☁️ DATEN IN CLOUD GESPEICHERT - Sofort für alle Benutzer weltweit verfügbar!`);
      } else {
        console.log(`💾 DATEN LOKAL GESPEICHERT - Verfügbar im gleichen Browser`);
      }
    } catch (error) {
      setUploadStatus(prev => ({ ...prev, [fileType]: 'error' }));
      console.error('❌ Upload error:', error);
    } finally {
      setUploading(prev => ({ ...prev, [fileType]: false }));
      // Status nach 5 Sekunden zurücksetzen
      setTimeout(() => {
        setUploadStatus(prev => ({ ...prev, [fileType]: null }));
      }, 5000);
    }
  };

  const handleSaveAccessCodes = () => {
    const newCodes = {
      [accessCodes.user]: { role: 'user', name: 'Handelsvertreter' },
      [accessCodes.admin]: { role: 'admin', name: 'Administrator' }
    };
    updateAccessCodes(newCodes);
    setUploadStatus(prev => ({ ...prev, accessCodes: 'success' }));
    setTimeout(() => {
      setUploadStatus(prev => ({ ...prev, accessCodes: null }));
    }, 3000);
  };
  
  const handleDeleteFile = (fileType) => {
    console.log(`🗑️ Datei ${fileType} wird zurückgesetzt...`);
    setUploadStatus(prev => ({ ...prev, [fileType]: 'deleted' }));
    setTimeout(() => {
      setUploadStatus(prev => ({ ...prev, [fileType]: null }));
    }, 2000);
  };
  
  const handleResetAllData = () => {
    if (window.confirm('⚠️ WICHTIG: Sind Sie sicher, dass Sie alle Daten für alle Benutzer zurücksetzen wollen? Diese Aktion kann nicht rückgängig gemacht werden!')) {
      resetAllGlobalData();
      setUploadStatus(prev => ({ ...prev, globalReset: 'success' }));
      setTimeout(() => {
        setUploadStatus(prev => ({ ...prev, globalReset: null }));
      }, 3000);
    }
  };
  
  const handleSaveSupabaseConfig = () => {
    if (supabaseConfig.url && supabaseConfig.key) {
        updateSupabaseConfig(supabaseConfig.url, supabaseConfig.key);
    }
  };

  const hasRealData = dataSource !== 'empty';
  
  const getProviderCount = (fileType) => {
    if (fileType === 'terminMarkt') return terminMarktData.length;
    if (fileType === 'spotMarkt') return spotMarktData.length;
    if (fileType === 'provisions') return provisionData.length;
    return 0;
  };

  // Datenprüfung durchführen beim Laden
  useEffect(() => {
    if (hasRealData) {
      performDataValidation();
    }
  }, [hasRealData, performDataValidation]);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#1e314b] mb-2">Administration</h1>
        <p className="text-gray-600">
          Verwalten Sie die drei Hauptdateien für das Energieversorger-Portal.
        </p>
      </div>

      {/* Supabase Konfigurations-Status */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`rounded-xl border p-6 ${supabaseStatus.connected ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}
      >
        <div className="flex items-center space-x-3 mb-4">
          <SafeIcon icon={supabaseStatus.connected ? FiCloud : FiAlertTriangle} className={`text-xl ${supabaseStatus.connected ? 'text-green-500' : 'text-orange-500'}`} />
          <div>
            <h3 className={`font-semibold ${supabaseStatus.connected ? 'text-green-800' : 'text-orange-800'}`}>
              {supabaseStatus.connected ? 'Supabase Verbunden' : 'Supabase Verbindung Prüfen'}
            </h3>
            <p className={`text-sm ${supabaseStatus.connected ? 'text-green-600' : 'text-orange-600'}`}>
              {supabaseStatus.connected 
                ? 'Das System ist erfolgreich mit der Datenbank verbunden.' 
                : 'Bitte prüfen Sie die Verbindungsdaten.'}
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <h4 className="font-medium text-gray-900 mb-3">Verbindungsdaten</h4>
            
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Project URL</label>
                <input 
                  type="text" 
                  value={supabaseConfig.url}
                  onChange={(e) => setSupabaseConfig(prev => ({ ...prev, url: e.target.value }))}
                  placeholder="https://your-project.supabase.co"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-orange-500 bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Anon Public Key</label>
                <input 
                  type="password" 
                  value={supabaseConfig.key}
                  onChange={(e) => setSupabaseConfig(prev => ({ ...prev, key: e.target.value }))}
                  placeholder="Key..."
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-orange-500 bg-gray-50"
                />
              </div>
              <button 
                  onClick={handleSaveSupabaseConfig}
                  className="w-full bg-[#1e314b] text-white py-2 rounded font-medium hover:bg-[#2c425e] transition-colors text-sm flex items-center justify-center"
              >
                  <SafeIcon icon={FiRefreshCw} className="mr-2" />
                  Verbindung neu aufbauen
              </button>
            </div>
          </div>

          <div className={`${supabaseStatus.connected ? 'bg-green-100' : 'bg-orange-100'} p-4 rounded-lg`}>
              <h4 className={`font-medium ${supabaseStatus.connected ? 'text-green-900' : 'text-orange-900'} mb-2`}>
                 Status: {supabaseStatus.connected ? 'Aktiv' : 'Getrennt'}
              </h4>
              <p className={`text-sm ${supabaseStatus.connected ? 'text-green-700' : 'text-orange-700'} mb-3`}>
                  {supabaseStatus.error ? `Fehler: ${supabaseStatus.error}` : supabaseStatus.connected ? 'Multi-User Sync ist aktiv.' : 'Verbindung wird aufgebaut oder ist fehlerhaft.'}
              </p>
              {supabaseStatus.connected && (
                 <div className="flex items-center text-green-700 text-sm">
                    <SafeIcon icon={FiCheck} className="mr-2" />
                    Datenbank erreichbar
                 </div>
              )}
          </div>
        </div>
      </motion.div>

      {/* Datenquellen-Status */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`rounded-xl border p-4 ${supabaseStatus.connected ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`}
      >
        <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
            <SafeIcon icon={supabaseStatus.connected ? FiCloud : FiDatabase} className={`text-xl ${supabaseStatus.connected ? 'text-green-500' : 'text-blue-500'}`} />
            <div>
                <h3 className={`font-semibold ${supabaseStatus.connected ? 'text-green-800' : 'text-blue-800'}`}>
                {supabaseStatus.connected ? '☁️ Cloud-Datenquelle aktiv' : '💾 Lokale Datenquelle aktiv'}
                </h3>
                <p className={`text-sm ${supabaseStatus.connected ? 'text-green-600' : 'text-blue-600'}`}>
                {supabaseStatus.connected ? (
                    <>☁️ Echtzeit-Daten für alle Benutzer weltweit. {terminMarktData.length + spotMarktData.length} Versorger in Cloud gespeichert. Multi-User-System aktiv!</>
                ) : (
                    <>💾 Daten im gleichen Browser gespeichert. {terminMarktData.length + spotMarktData.length} Versorger lokal.</>
                )}
                </p>
            </div>
            </div>
            {supabaseStatus.connected && (
                <button 
                    onClick={disconnectSupabase}
                    className="text-xs text-red-600 hover:text-red-800 underline"
                >
                    Verbindung trennen
                </button>
            )}
        </div>
      </motion.div>

      {/* Datei-Management Sektion */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
      >
        <div className="flex items-center space-x-3 mb-6">
          <div className="bg-[#9b061b] text-white w-10 h-10 rounded-lg flex items-center justify-center">
            <SafeIcon icon={supabaseStatus.connected ? FiDatabaseCloud : FiUpload} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-[#1e314b]">
                {supabaseStatus.connected ? 'Cloud-Datei-Management' : 'Lokales Datei-Management'}
            </h2>
            <p className="text-sm text-gray-600">
              Laden Sie die drei requireden Dateien hoch.
              {supabaseStatus.connected 
                ? ' Dateien werden in der Cloud gespeichert und sind sofort für alle Benutzer weltweit verfügbar.' 
                : ' Dateien bleiben im Browser gespeichert und sind für alle Benutzer im gleichen Browser verfügbar.'}
            </p>
          </div>
        </div>

        <div className="space-y-6">
          {fileTypes.map((fileType) => {
            if (!fileType.active) return null;
            
            const currentFile = files[fileType.key];
            const isUploading = uploading[fileType.key];
            const status = uploadStatus[fileType.key];
            const hasUploadedFile = hasFile(fileType.key);
            const providerCount = getProviderCount(fileType.key);

            return (
              <motion.div 
                key={fileType.key}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: fileTypes.indexOf(fileType) * 0.1 }}
                className={`border rounded-lg p-6 hover:shadow-md transition-shadow ${
                  hasUploadedFile ? 'border-green-300 bg-green-50' : 'border-gray-200'
                }`}
              >
                <div className="flex items-start space-x-4 mb-4">
                  <SafeIcon icon={FiFile} className="text-[#1e314b] text-xl mt-1" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="font-medium text-gray-900 text-lg">{fileType.label}</h3>
                        {hasUploadedFile && (
                            <div className="flex items-center space-x-2">
                                <SafeIcon icon={supabaseStatus.connected ? FiCloud : FiCheck} className="text-green-500" />
                                <span className="text-sm text-green-600 font-medium">
                                    {supabaseStatus.connected ? 'Cloud aktiv' : 'Lokal aktiv'}
                                </span>
                            </div>
                        )}
                    </div>
                    <p className="text-sm text-gray-600 mb-3">{fileType.description}</p>
                    <p className="text-xs text-gray-500 mb-2">Erwartetes Sheet: {fileType.expectedSheet}</p>
                    
                    {/* Anzahl der geladenen Versorger */}
                    {providerCount > 0 && (
                        <div className="text-sm text-green-700 font-medium mb-3">
                            {providerCount} Versorger geladen ({supabaseStatus.connected ? 'cloud' : 'lokal'} verfügbar)
                        </div>
                    )}

                    {currentFile && (
                      <div className="text-xs text-gray-500 space-y-1 bg-white p-3 rounded border border-gray-200">
                        <div className="flex items-center space-x-2">
                          <SafeIcon icon={FiFile} className="text-xs" />
                          <span className="truncate">{currentFile.name}</span>
                          <span className="text-gray-400">({(currentFile.size / 1024).toFixed(1)} KB)</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <SafeIcon icon={FiCalendar} className="text-xs" />
                          <span>
                            {new Date(currentFile.uploadDate).toLocaleString('de-DE')}
                          </span>
                          {supabaseStatus.connected && (
                            <span className="text-green-600 ml-2">☁️ Cloud</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  <label 
                    className={`relative block w-full px-4 py-3 border-2 border-dashed rounded-lg text-center cursor-pointer transition-all ${
                      isUploading ? 'border-blue-300 bg-blue-50' :
                      status === 'success' ? 'border-green-300 bg-green-50' :
                      status === 'error' ? 'border-red-300 bg-red-50' :
                      status === 'deleted' ? 'border-orange-300 bg-orange-50' :
                      'border-gray-300 hover:border-[#9b061b] hover:bg-gray-50'
                    }`}
                  >
                    <input 
                      type="file" 
                      accept={fileType.accept}
                      onChange={(e) => handleFileUpload(fileType.key, e)}
                      disabled={isUploading}
                      className="sr-only"
                    />
                    <div className="flex flex-col items-center space-y-2">
                      {isUploading ? (
                        <>
                          <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                          <span className="text-sm text-blue-600">
                            {supabaseStatus.connected ? 'Wird in Cloud hochgeladen...' : 'Wird hochgeladen...'}
                          </span>
                        </>
                      ) : status === 'success' ? (
                        <>
                          <SafeIcon icon={FiCheck} className="text-green-500 text-2xl" />
                          <span className="text-sm text-green-600">
                            Erfolgreich hochgeladen und für alle verfügbar!
                          </span>
                        </>
                      ) : status === 'error' ? (
                        <>
                          <SafeIcon icon={FiX} className="text-red-500 text-2xl" />
                          <span className="text-sm text-red-600">Upload fehlgeschlagen</span>
                        </>
                      ) : status === 'deleted' ? (
                        <>
                          <SafeIcon icon={FiTrash2} className="text-orange-500 text-2xl" />
                          <span className="text-sm text-orange-600">Datei zurückgesetzt</span>
                        </>
                      ) : (
                        <>
                          <SafeIcon icon={FiUpload} className="text-gray-400 text-2xl" />
                          <span className="text-sm text-gray-600">
                            {hasUploadedFile ? 'Datei ersetzen' : 'Datei hochladen'}
                          </span>
                          <span className="text-xs text-gray-500">
                            {hasUploadedFile ? 'Neue Datei hochladen' : `Erlaubte Formate: ${fileType.accept}`}
                          </span>
                        </>
                      )}
                    </div>
                  </label>
                  
                  {hasUploadedFile && (
                    <div className="flex justify-end">
                      <button 
                        onClick={() => handleDeleteFile(fileType.key)}
                        className="text-xs text-red-600 hover:text-red-800 flex items-center space-x-1"
                      >
                        <SafeIcon icon={FiTrash2} className="text-xs" />
                        <span>Zurücksetzen</span>
                      </button>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </motion.div>

      {/* DATENPRÜFUNG / FEHLERKONTROLLE */}
      {hasRealData && (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
            <div className="flex items-center space-x-3 mb-6">
                <div className="bg-orange-500 text-white w-10 h-10 rounded-lg flex items-center justify-center">
                    <SafeIcon icon={FiAlertTriangle} />
                </div>
                <div>
                    <h2 className="text-lg font-semibold text-[#1e314b]">Datenprüfung / Fehlerkontrolle</h2>
                    <p className="text-sm text-gray-600">
                        Automatische Prüfung der Datenstruktur und Matching-Ergebnisse
                    </p>
                </div>
            </div>

            {/* Fehlerübersicht */}
            <div className="space-y-4">
                {/* Strukturprüfung */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className={`border rounded-lg p-4 ${dataValidation.terminmarktErrors?.length > 0 ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'}`}>
                        <div className="flex items-center space-x-2 mb-2">
                            <SafeIcon icon={dataValidation.terminmarktErrors?.length > 0 ? FiX : FiCheck} className={dataValidation.terminmarktErrors?.length > 0 ? 'text-red-500' : 'text-green-500'} />
                            <span className="font-medium text-gray-800">Terminmarkt-Struktur</span>
                        </div>
                        <p className="text-sm text-gray-700">
                            {dataValidation.terminmarktErrors?.length > 0 ? `${dataValidation.terminmarktErrors.length} Fehler` : 'Struktur OK'}
                        </p>
                    </div>
                    <div className={`border rounded-lg p-4 ${dataValidation.spotmarktErrors?.length > 0 ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'}`}>
                        <div className="flex items-center space-x-2 mb-2">
                            <SafeIcon icon={dataValidation.spotmarktErrors?.length > 0 ? FiX : FiCheck} className={dataValidation.spotmarktErrors?.length > 0 ? 'text-red-500' : 'text-green-500'} />
                            <span className="font-medium text-gray-800">Spotmarkt-Struktur</span>
                        </div>
                        <p className="text-sm text-gray-700">
                            {dataValidation.spotmarktErrors?.length > 0 ? `${dataValidation.spotmarktErrors.length} Fehler` : 'Struktur OK'}
                        </p>
                    </div>
                    <div className={`border rounded-lg p-4 ${dataValidation.provisionsErrors?.length > 0 ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'}`}>
                        <div className="flex items-center space-x-2 mb-2">
                            <SafeIcon icon={dataValidation.provisionsErrors?.length > 0 ? FiX : FiCheck} className={dataValidation.provisionsErrors?.length > 0 ? 'text-red-500' : 'text-green-500'} />
                            <span className="font-medium text-gray-800">Provisions-Struktur</span>
                        </div>
                        <p className="text-sm text-gray-700">
                            {dataValidation.provisionsErrors?.length > 0 ? `${dataValidation.provisionsErrors.length} Fehler` : 'Struktur OK'}
                        </p>
                    </div>
                </div>

                {/* Matching-Fehler */}
                {dataValidation.matchingErrors?.length > 0 && (
                    <div className="border border-orange-300 bg-orange-50 rounded-lg p-4">
                        <div className="flex items-center space-x-2 mb-3">
                            <SafeIcon icon={FiAlertTriangle} className="text-orange-500" />
                            <h4 className="font-medium text-orange-800">Matching-Probleme</h4>
                        </div>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                            {dataValidation.matchingErrors.map((error, index) => (
                                <div key={index} className="text-sm text-orange-700 bg-orange-100 p-2 rounded">
                                    {error}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                
                {/* Duplikat-Warnungen */}
                {dataValidation.duplicateWarnings?.length > 0 && (
                    <div className="border border-yellow-300 bg-yellow-50 rounded-lg p-4">
                        <div className="flex items-center space-x-2 mb-3">
                            <SafeIcon icon={FiAlertTriangle} className="text-yellow-500" />
                            <h4 className="font-medium text-yellow-800">Duplikat-Warnungen</h4>
                        </div>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                            {dataValidation.duplicateWarnings.map((warning, index) => (
                                <div key={index} className="text-sm text-yellow-700 bg-yellow-100 p-2 rounded">
                                    {warning}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Keine Fehler */}
                {dataValidation.matchingErrors?.length === 0 && 
                 dataValidation.duplicateWarnings?.length === 0 &&
                 dataValidation.terminmarktErrors?.length === 0 && 
                 dataValidation.spotmarktErrors?.length === 0 && 
                 dataValidation.provisionsErrors?.length === 0 && (
                    <div className="border border-green-300 bg-green-50 rounded-lg p-4">
                        <div className="flex items-center space-x-2">
                            <SafeIcon icon={FiCheck} className="text-green-500" />
                            <span className="font-medium text-green-800">Alle Prüfungen bestanden</span>
                        </div>
                        <p className="text-sm text-green-700 mt-2">
                            Die Datenstruktur ist korrekt und alle Matches wurden gefunden.
                        </p>
                    </div>
                )}
            </div>
        </motion.div>
      )}

      {/* Globale Daten zurücksetzen */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-red-50 border border-red-200 rounded-xl p-6"
      >
        <div className="flex items-center space-x-3 mb-4">
          <div className="bg-red-500 text-white w-10 h-10 rounded-lg flex items-center justify-center">
            <SafeIcon icon={FiRefreshCw} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-red-800">
              {supabaseStatus.connected ? 'Cloud-Daten zurücksetzen' : 'Lokale Daten zurücksetzen'}
            </h2>
            <p className="text-sm text-red-600">
              {supabaseStatus.connected 
                ? 'Alle hochgeladenen Daten für alle Benutzer aus der Cloud löschen. Diese Aktion kann nicht rückgängig gemacht werden!'
                : 'Alle lokalen Daten löschen. Diese Aktion kann nicht rückgängig gemacht werden!'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-red-700 font-medium">
              ⚠️ Dies entfernt alle Daten und macht sie für alle Benutzer unzugänglich!
            </p>
            <p className="text-xs text-red-600 mt-1">
              Nur verwenden, wenn alle Daten komplett neu hochgeladen werden sollen.
            </p>
          </div>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleResetAllData}
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
          >
            <SafeIcon icon={FiTrash2} />
            <span>Alle {supabaseStatus.connected ? 'Cloud-' : ''}Daten löschen</span>
          </motion.button>
        </div>
        
        {uploadStatus.globalReset === 'success' && (
          <div className="mt-4 p-3 bg-green-100 border border-green-300 rounded-lg">
            <div className="flex items-center space-x-2 text-green-700">
              <SafeIcon icon={FiCheck} />
              <span className="text-sm font-medium">
                Alle {supabaseStatus.connected ? 'Cloud-' : ''}Daten wurden erfolgreich zurückgesetzt.
              </span>
            </div>
          </div>
        )}
      </motion.div>

      {/* Zugangscodes verwalten */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
      >
        <div className="flex items-center space-x-3 mb-6">
          <div className="bg-[#1e314b] text-white w-10 h-10 rounded-lg flex items-center justify-center">
            <SafeIcon icon={FiKey} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-[#1e314b]">Zugangscodes</h2>
            <p className="text-sm text-gray-600">Verwalten Sie die Zugangscodes für Benutzer und Administratoren.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Benutzer-Code (Handelsvertreter)</label>
            <input 
              type="text" 
              value={accessCodes.user} 
              onChange={(e) => setAccessCodes(prev => ({ ...prev, user: e.target.value }))} 
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#9b061b] focus:border-[#9b061b]" 
              placeholder="Benutzer-Zugangscode" 
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Admin-Code (Administrator)</label>
            <input 
              type="text" 
              value={accessCodes.admin} 
              onChange={(e) => setAccessCodes(prev => ({ ...prev, admin: e.target.value }))} 
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#9b061b] focus:border-[#9b061b]" 
              placeholder="Admin-Zugangscode" 
            />
          </div>
        </div>

        <div className="mt-6 flex items-center space-x-4">
          <motion.button 
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleSaveAccessCodes}
            className="inline-flex items-center px-4 py-2 bg-[#1e314b] text-white rounded-lg hover:bg-[#152738] transition-colors"
          >
            <SafeIcon icon={FiSave} className="mr-2" />
            Codes speichern
          </motion.button>
          {uploadStatus.accessCodes === 'success' && (
            <div className="flex items-center text-green-600">
              <SafeIcon icon={FiCheck} className="mr-1" />
              <span className="text-sm">Zugangscodes aktualisiert!</span>
            </div>
          )}
        </div>
      </motion.div>

      {/* System-Status */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
      >
        <div className="flex items-center space-x-3 mb-6">
          <div className="bg-green-500 text-white w-10 h-10 rounded-lg flex items-center justify-center">
            <SafeIcon icon={supabaseStatus.connected ? FiDatabaseCloud : FiDatabase} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-[#1e314b]">System-Status</h2>
            <p className="text-sm text-gray-600">Übersicht über den aktuellen Systemzustand.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className={`border rounded-lg p-4 ${
            supabaseStatus.connected ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'
          }`}>
            <div className="flex items-center space-x-2 mb-2">
              <SafeIcon icon={supabaseStatus.connected ? FiCloud : FiDatabase} className={`${
                supabaseStatus.connected ? 'text-green-500' : 'text-blue-500'
              }`} />
              <span className="font-medium text-gray-800">
                {supabaseStatus.connected ? 'Cloud-Dateien' : 'Lokale Dateien'}
              </span>
            </div>
            <p className="text-sm text-gray-700">
              {Object.keys(files).filter(key => hasFile(key)).length} von 3 Dateien {
                supabaseStatus.connected ? 'in Cloud' : 'lokal'
              } gespeichert
            </p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <SafeIcon icon={FiDatabase} className="text-blue-500" />
              <span className="font-medium text-blue-800">Versorger</span>
            </div>
            <p className="text-sm text-blue-700">
              {terminMarktData.length + spotMarktData.length} {
                supabaseStatus.connected ? 'Cloud' : 'lokal'
              } geladen
            </p>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <SafeIcon icon={FiShield} className="text-purple-500" />
              <span className="font-medium text-purple-800">Multi-User</span>
            </div>
            <p className="text-sm text-purple-700">
              {supabaseStatus.connected ? '✅ Aktiv' : '❌ Inaktiv'}
            </p>
          </div>
        </div>

        {supabaseStatus.connected && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center space-x-2 text-green-700">
              <SafeIcon icon={FiCloud} className="text-green-500" />
              <span className="text-sm font-medium">
                ☁️ Cloud-Modus aktiv: Daten sind weltweit verfügbar und werden automatisch synchronisiert.
              </span>
            </div>
          </div>
        )}

        {!supabaseStatus.connected && (
          <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="flex items-center space-x-2 text-orange-700">
              <SafeIcon icon={FiAlertTriangle} className="text-orange-500" />
              <span className="text-sm font-medium">
                ⚠️ Multi-User-System nicht aktiv. Daten sind nur im gleichen Browser verfügbar.
              </span>
            </div>
          </div>
        )}
      </motion.div>

      {/* Setup Guide Modal */}
      {showSetupGuide && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <SetupGuide onComplete={() => setShowSetupGuide(false)} />
          </div>
        </div>
      )}
    </div>
  );
}